/**
 * Shared inline-tagger widgets used to backfill vendor data in the natural
 * operator workflow (inside Query Workspace VendorMultiPicker rows AND
 * inside the Vendors module list/cards).
 *
 *   • AccommodationTypeTagger  — multi-select chips for Hotel vendors
 *     (Hotel + Boutique, Resort + Boutique, Homestay + Boutique, …)
 *   • ContactTagger            — inline name / email / phone editor
 *
 * Both PATCH the vendor optimistically and toast on success/fail.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ACC_TYPES = ['Hotel', 'Resort', 'Villa', 'Boutique', 'Homestay', 'Luxury Chain'];

export const AccommodationTypeTagger = ({ vendor, onTagged }) => {
  const [open, setOpen] = useState(false);
  const initial = (vendor.accommodation_types && vendor.accommodation_types.length)
    ? vendor.accommodation_types
    : (vendor.accommodation_type ? [vendor.accommodation_type] : []);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const next = (vendor.accommodation_types && vendor.accommodation_types.length)
      ? vendor.accommodation_types
      : (vendor.accommodation_type ? [vendor.accommodation_type] : []);
    setValue(next);
  }, [vendor.accommodation_type, vendor.accommodation_types]);

  const commit = async (nextList) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/pollen/vendors/${vendor.id}/accommodation-type`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ accommodation_types: nextList }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Tag failed');
      } else {
        setValue(nextList);
        vendor.accommodation_types = nextList;
        vendor.accommodation_type = nextList[0] || '';
        onTagged && onTagged(nextList);
        toast.success(nextList.length ? `Tagged: ${nextList.join(', ')}` : 'Cleared');
      }
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const summary = value.length ? `Type: ${value.join(' / ')}` : '+ Tag type';
  return (
    <div className="mt-0" onClick={(e) => e.stopPropagation()}>
      {!open ? (
        <button type="button"
          className={`text-[10px] px-1.5 py-0.5 rounded border ${
            value.length
              ? 'bg-foreground/5 border-foreground/20 text-foreground'
              : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
          }`}
          onClick={() => setOpen(true)}
          data-testid={`acc-tagger-${vendor.id}`}>
          {summary}
        </button>
      ) : (
        <div className="rounded-md border bg-background p-1.5 space-y-1.5"
          data-testid={`acc-tagger-form-${vendor.id}`}>
          <div className="flex flex-wrap gap-1">
            {ACC_TYPES.map(o => {
              const active = value.includes(o);
              return (
                <button key={o} type="button" disabled={saving}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                    active
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-white text-foreground border-border'
                  }`}
                  onClick={() => commit(active ? value.filter(x => x !== o) : [...value, o])}
                  data-testid={`acc-tagger-opt-${vendor.id}-${o.toLowerCase().replace(/\s+/g, '-')}`}>
                  {o}
                </button>
              );
            })}
          </div>
          <div className="flex gap-1.5">
            {value.length > 0 && (
              <Button size="sm" variant="ghost" className="h-7 text-[10px] text-destructive"
                disabled={saving} onClick={() => commit([])}>
                Clear all
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-[10px] ml-auto"
              onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ContactTagger = ({ vendor, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(vendor.name || '');
  const [email, setEmail] = useState(vendor.email || '');
  const [phone, setPhone] = useState(vendor.phone || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setName(vendor.name || '');
    setEmail(vendor.email || '');
    setPhone(vendor.phone || '');
  }, [vendor.name, vendor.email, vendor.phone]);

  const dirty = (name.trim() !== (vendor.name || '').trim())
    || (email.trim() !== (vendor.email || '').trim())
    || (phone.trim() !== (vendor.phone || '').trim());

  const save = async () => {
    if (!dirty) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/pollen/vendors/${vendor.id}/contact`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Save failed');
        return;
      }
      if (data.vendor) {
        vendor.name = data.vendor.name;
        vendor.email = data.vendor.email;
        vendor.phone = data.vendor.phone;
      }
      onUpdated && onUpdated(data.vendor || vendor);
      toast.success('Contact updated');
      setOpen(false);
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const hasEmail = !!(vendor.email || '').trim();
  const hasPhone = !!(vendor.phone || '').trim();
  const missing = !hasEmail || !hasPhone;

  if (!open) {
    return (
      <button type="button"
        className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${
          missing
            ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
            : 'bg-foreground/5 border-foreground/20 text-foreground hover:bg-foreground/10'
        }`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`contact-tagger-${vendor.id}`}>
        {missing
          ? `+ Add ${!hasEmail && !hasPhone ? 'email + phone' : !hasEmail ? 'email' : 'phone'}`
          : 'Edit contact'}
      </button>
    );
  }
  return (
    <div className="rounded-md border bg-background p-2 space-y-1.5"
      onClick={(e) => e.stopPropagation()}
      data-testid={`contact-tagger-form-${vendor.id}`}>
      <Input className="h-8 text-xs" placeholder="Vendor name"
        value={name} onChange={e => setName(e.target.value)}
        data-testid={`contact-name-${vendor.id}`} />
      <Input className="h-8 text-xs" placeholder="email@example.com"
        value={email} onChange={e => setEmail(e.target.value)}
        data-testid={`contact-email-${vendor.id}`} />
      <Input className="h-8 text-xs" placeholder="+91 9xxxxxxxxx"
        value={phone} onChange={e => setPhone(e.target.value)}
        data-testid={`contact-phone-${vendor.id}`} />
      <div className="flex gap-1.5">
        <Button size="sm" className="h-8 text-xs flex-1" disabled={saving || !dirty}
          onClick={save} data-testid={`contact-save-${vendor.id}`}>
          {saving ? '…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
